/**
 * DashboardView - Revenue reporting and control center
 * 
 * Features:
 * - Date range selection (single date or range)
 * - Toggle between order accepted date vs delivery date
 * - Total revenue display
 * - Bar chart with auto-aggregation based on range size
 * - Roll-up controls (daily → weekly → monthly → yearly)
 */
import React, { useState, useEffect, useCallback, useMemo, useSyncExternalStore } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import RangeCalendar from '@/components/RangeCalendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CalendarIcon, DollarSign, Package, TrendingUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  getGrossBySourceReport,
  getNetBySourceReport,
  getOrdersBySourceReport,
  getOrdersBySubsourceReport,
  getRevenueReport,
} from '@/api/reports';
import { getPublicShopBranding } from '@/api/shop';
import usePipeline from '@/hooks/usePipeline';
import useDashboardRead from '@/hooks/useDashboardRead';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { fillRevenueBuckets, getDefaultAggregation, grossSourceReport, netSourceReport, revenueReport, sourceReport } from '@/utils/dashboardReports';
import PipelineCard from '@/components/PipelineCard';
import SourceBreakdown from '@/components/SourceBreakdown';
import { formatCurrency } from '@/utils/orderValue';

// Format chart X-axis labels based on aggregation
const formatXAxisLabel = (dateStr, aggregation) => {
  if (!dateStr) return '';
  try {
    const date = parseISO(dateStr);
    switch (aggregation) {
      case 'daily':
        return format(date, 'MMM d');
      case 'weekly':
        return format(date, 'MMM d');
      case 'monthly':
        return format(date, 'MMM yyyy');
      case 'yearly':
        return format(date, 'yyyy');
      default:
        return dateStr;
    }
  } catch {
    return dateStr;
  }
};

// Custom tooltip for the chart
const CustomTooltip = ({ active, payload, label, aggregation }) => {
  if (active && payload && payload.length) {
    const formattedLabel = formatXAxisLabel(label, aggregation);
    return (
      <div className="bg-white border border-stone-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-stone-900">{formattedLabel}</p>
        <p className="text-sm text-emerald-600 font-semibold">
          {formatCurrency(payload[0].value)}
        </p>
        <p className="text-xs text-stone-500">
          {payload[0].payload.orderCount} order{payload[0].payload.orderCount !== 1 ? 's' : ''}
        </p>
      </div>
    );
  }
  return null;
};

const DashboardView = () => {
  const [activeTab, setActiveTab] = useState('operations');


  // Committed date range (what's actually used for queries)
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });
  
  // Temporary selection state (while calendar is open)
  const [tempRange, setTempRange] = useState({ from: undefined, to: undefined });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarKey, setCalendarKey] = useState(0); // Force re-render

  // Date type toggle: 'entered' (order_accepted_date) or 'delivered' (delivery_date)
  const [dateType, setDateType] = useState('entered');
  
  // Shop timezone
  const revision = useSyncExternalStore(
    subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision,
  );
  const [branding, setBranding] = useState(null);
  const shopTimezone = branding?.revision === revision ? branding.timezone : 'America/Los_Angeles';

  // Pipeline data (Operations tab) — today + next 6 days, shop timezone
  const { days: pipelineDays, loading: pipelineLoading, error: pipelineError, refresh: retryPipeline } = usePipeline(shopTimezone);

  // Aggregation level
  const aggregation = getDefaultAggregation(dateRange.from, dateRange.to);

  // Fetch shop settings (including timezone) on mount
  useEffect(() => {
    let active = true;
    const fetchShopSettings = async () => {
      try {
        const branding = await getPublicShopBranding();
        if (active && revision === getActiveSessionRevision() && branding.timezone) {
          setBranding({ revision, timezone: branding.timezone });
        }
      } catch (e) {
        console.error('Error fetching shop settings:', e);
      }
    };
    fetchShopSettings();
    return () => { active = false; };
  }, [revision]);

  const params = useMemo(() => ({
      start_date: format(dateRange.from, "yyyy-MM-dd"),
      end_date: format(dateRange.to, "yyyy-MM-dd"),
      date_field: dateType === 'entered' ? 'order_accepted_date' : 'delivery_date',
      aggregation: aggregation,
      timezone: shopTimezone,
  }), [dateRange.from, dateRange.to, dateType, aggregation, shopTimezone]);
  const queryKey = JSON.stringify(params);
  const loadRevenue = useCallback(async () => revenueReport(await getRevenueReport(params)), [params]);
  const loadSources = useCallback(async () => sourceReport(await getOrdersBySourceReport(params)), [params]);
  const loadGrossSources = useCallback(async () => grossSourceReport(await getGrossBySourceReport(params)), [params]);
  const loadNetSources = useCallback(async () => netSourceReport(await getNetBySourceReport(params)), [params]);
  const loadFlorists = useCallback(async () => sourceReport(await getOrdersBySubsourceReport(params)), [params]);
  const revenue = useDashboardRead(queryKey, loadRevenue);
  const sources = useDashboardRead(queryKey, loadSources);
  const grossSources = useDashboardRead(queryKey, loadGrossSources);
  const netSources = useDashboardRead(queryKey, loadNetSources);
  const florists = useDashboardRead(queryKey, loadFlorists);
  // Identical net and gross figures are the correct result until a charge is
  // entered, so the row says which of the two reasons applies. A Source counts
  // as configured if any day in the range falls under an entered rule set, so
  // only a row with no coverage at all is marked (TCA-26102605).
  const netMarker = useCallback(row => (row && row.covered_days === 0
    ? 'No deductions configured. Net equals gross.'
    : null), []);
  const loading = revenue.status === 'loading';
  const revenueData = revenue.data?.buckets || [];
  const totalRevenue = revenue.data?.total_revenue;
  const totalOrders = revenue.data?.total_orders;
  const filledRevenueData = fillRevenueBuckets(revenueData, dateRange.from, dateRange.to, aggregation);

  // Format date range display
  const dateRangeDisplay = useMemo(() => {
    if (!dateRange.from) return 'Select date range';
    if (!dateRange.to || dateRange.from.getTime() === dateRange.to.getTime()) {
      return format(dateRange.from, 'MMM d, yyyy');
    }
    return `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
  }, [dateRange]);

  // Average order value
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  return (
    <div className="min-h-screen bg-stone-50 p-4 md:p-8" data-testid="dashboard-view">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Dashboard</h1>
        </div>

        {/* Tab navigation */}
        <div className="border-b border-stone-200 -mt-2">
          <nav className="flex gap-6">
            {[{ id: 'operations', label: 'Operations' }, { id: 'revenue', label: 'Revenue' }].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-stone-900 text-stone-900'
                    : 'border-transparent text-stone-500 hover:text-stone-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'operations' && (
          <PipelineCard days={pipelineDays} loading={pipelineLoading} error={pipelineError} onRetry={retryPipeline} />
        )}

        {activeTab === 'revenue' && (<>
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3 justify-end">
            {/* Date Type Toggle */}
            <div className="flex rounded-lg border border-stone-200 bg-white p-1">
              <button
                onClick={() => setDateType('entered')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  dateType === 'entered'
                    ? 'bg-stone-900 text-white'
                    : 'text-stone-600 hover:text-stone-900'
                }`}
                data-testid="date-type-entered"
              >
                Date Order Entered
              </button>
              <button
                onClick={() => setDateType('delivered')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  dateType === 'delivered'
                    ? 'bg-stone-900 text-white'
                    : 'text-stone-600 hover:text-stone-900'
                }`}
                data-testid="date-type-delivered"
              >
                Date Order Delivered
              </button>
            </div>

            {/* Date Range Picker */}
            <Popover open={calendarOpen} onOpenChange={(open) => {
              if (open) {
                // When opening, clear temp selection and force calendar re-render
                setTempRange({ from: undefined, to: undefined });
                setCalendarKey(k => k + 1);
              } else {
                // When closing without Apply, discard temp selection
                setTempRange({ from: undefined, to: undefined });
              }
              setCalendarOpen(open);
            }}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="justify-start text-left font-normal h-9 px-3"
                  data-testid="date-range-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  <span className="text-sm">{dateRangeDisplay}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="p-3 border-b border-stone-200 text-xs text-stone-500 text-center">
                  {!tempRange.from 
                    ? 'Click to select start date' 
                    : (!tempRange.to || tempRange.from.getTime() === tempRange.to.getTime()) 
                      ? 'Click to select end date' 
                      : `${format(tempRange.from, 'MMM d')} - ${format(tempRange.to, 'MMM d, yyyy')} (click any date to restart)`}
                </div>
                <RangeCalendar
                  key={calendarKey}
                  value={tempRange}
                  onChange={setTempRange}
                  numberOfMonths={2}
                  defaultMonth={dateRange.from}
                />
                <div className="border-t border-stone-200 p-3 flex justify-end">
                  <Button
                    size="sm"
                    disabled={!tempRange.from}
                    onClick={() => {
                      if (tempRange.from) {
                        setDateRange({
                          from: tempRange.from,
                          to: tempRange.to || tempRange.from,
                        });
                      }
                      setCalendarOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Gross Revenue</p>
                  <p className="text-2xl font-bold text-stone-900" data-testid="total-revenue">
                    {revenue.status === 'success' ? formatCurrency(totalRevenue) : loading ? 'Loading…' : 'Unavailable'}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Total Orders</p>
                  <p className="text-2xl font-bold text-stone-900" data-testid="total-orders">
                    {revenue.status === 'success' ? totalOrders : loading ? 'Loading…' : 'Unavailable'}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">Avg Order Value</p>
                  <p className="text-2xl font-bold text-stone-900" data-testid="avg-order-value">
                    {revenue.status === 'success' ? formatCurrency(avgOrderValue) : loading ? 'Loading…' : 'Unavailable'}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart Card */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Revenue Over Time</CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-500 capitalize">
                  {aggregation} view
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[300px] flex items-center justify-center text-stone-400">
                Loading...
              </div>
            ) : revenue.status === 'unavailable' ? (
              <p role="alert">Unavailable — <button type="button" onClick={revenue.refresh}>Try again</button></p>
            ) : revenueData.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-stone-400">
                No data for selected range
              </div>
            ) : (
              <div className="h-[300px]" data-testid="revenue-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filledRevenueData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#78716c' }}
                      tickFormatter={(value) => formatXAxisLabel(value, aggregation)}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#78716c' }}
                      tickFormatter={(value) => `$${value}`}
                      width={60}
                    />
                    <Tooltip content={<CustomTooltip aggregation={aggregation} />} />
                    <Bar
                      dataKey="revenue"
                      fill="#10b981"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={50}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-4">
            <div className="space-y-4 min-w-0">
              <SourceBreakdown title="Orders by Source" sources={sources.data} loading={sources.status === 'loading'} error={sources.status === 'unavailable'} onRetry={sources.refresh} />
              <SourceBreakdown title="Gross by Source" sources={grossSources.data} loading={grossSources.status === 'loading'} error={grossSources.status === 'unavailable'} onRetry={grossSources.refresh} valueKey="gross" formatValue={formatCurrency} valueType="money" verbatim />
              <SourceBreakdown title="Net by Source" sources={netSources.data} loading={netSources.status === 'loading'} error={netSources.status === 'unavailable'} onRetry={netSources.refresh} valueKey="net" formatValue={formatCurrency} valueType="money" markerFor={netMarker} verbatim />
            </div>
            <SourceBreakdown title="Sending Florists" sources={florists.data} loading={florists.status === 'loading'} error={florists.status === 'unavailable'} onRetry={florists.refresh} verbatim />
          </div>
        </>)}
      </div>
    </div>
  );
};

export default DashboardView;
